import { useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";

//Common
import FormBackGround from "../common/Forms/FormBackGround/FormBackGround";
import FormHeader from "../common/Forms/FormHeader/FormHeader";
import TextField from "../common/VFX-UI/TextField/TextField";
import { MaskedPhoneNumberField } from "../common/VFX-UI/Mask/Mask";
import ActionBar from "../common/ActionBar/ActionBar";
import { EMPTY_GUID } from "../../utility/constants/constants";
import useComponentGeneralStyles from "../styles";

//App
import { UserFormProps } from "../../types";
import { yupValidatePhone } from "../../utility/helpers/yupValidation";
import { Autocomplete, Chip, ConfirmationDialog } from "../common/VFX-UI";
import { LookupDto } from "../../app/model/api/types/dto";
import { ORGANIZATION_ADMIN, CORP_EMPLOYEE, SYSTEM_ADMIN, SUPER_ADMIN } from "./constants";
import { hasRole, roles} from "../../utility/helpers/claimHelper";
import { Label } from "reactstrap";

const validationSchema = Yup.object({
    userName: Yup.string().trim().required("User name is required."),
    firstName: Yup.string().required("The first name is required."),
    lastName: Yup.string().required("The last name is required."),
    email: Yup.string()
        .email("Enter a valid email")
        .required("Email is required."),
    phoneNumber: yupValidatePhone().required("Phone number is required."),
    roleId: Yup.array().min(1, "Role is required").required("Role is required"),
});

const UserForm = (props: UserFormProps) => {
    const { status, initialVal, onClose, handleOnSubmit, lookups } = props;
    const formRef = useRef<FormikProps<any>>(null);
    const { id } = initialVal;
    const IsNew = () => id === EMPTY_GUID;
    const title = IsNew() ? "New User" : "Edit User";
    const generalStyles = useComponentGeneralStyles();

    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
        useState(false);

    let lookupOptions: any = [];
    if (lookups) {
        if (
            initialVal.organizationId === null ||
            initialVal.organizationId === undefined ||
            initialVal.organizationId === EMPTY_GUID
        ) {
            lookupOptions = lookups
                ?.filter(
                    (item: LookupDto) =>
                        item.id !== ORGANIZATION_ADMIN.toLowerCase() &&
                        item.id !== CORP_EMPLOYEE.toLowerCase() &&
                        item.id !== SYSTEM_ADMIN.toLowerCase() &&
                        item.id !== SUPER_ADMIN.toLocaleLowerCase()
                )
                .map((item: LookupDto) => ({
                    value: item.id,
                    label: item.name,
                }));
        } else if (hasRole([roles.SYSTEM_ADMIN])) {
            lookupOptions = lookups?.map((item: LookupDto) => ({
                value: item.id,
                label: item.name,
            }));
        } else {
            lookupOptions = lookups
                ?.filter(
                    (item: LookupDto) =>
                        item.id === ORGANIZATION_ADMIN.toLowerCase() ||
                        item.id === CORP_EMPLOYEE.toLowerCase()
                )
                .map((item: LookupDto) => ({
                    value: item.id,
                    label: item.name,
                }));
        }
    }

    

    const saveInput = () => {
        if (formRef.current) {
            if (
                formRef.current?.values?.id !== EMPTY_GUID &&
                formRef.current?.values?.email !== initialVal.email
            )
                setIsConfirmationDialogOpen(true);
            else {
                setIsConfirmationDialogOpen(false);
                formRef.current.handleSubmit();
            }
        }
    };

    const toggleConfirmationDialogHandler = () => {
        setIsConfirmationDialogOpen(false);
    };

    const handleConfirmationDialog = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    return (
        <FormBackGround>
            <FormHeader title={title} onClose={onClose} />
            <Formik
                onSubmit={handleOnSubmit}
                initialValues={initialVal}
                validationSchema={validationSchema}
                innerRef={formRef}
            >
                {({ values }) => (
                    <Form noValidate>
                       <div className="d-flex justify-content-end mb-3 me-3">
                            <Chip label={values.isActive ? "Active" : "Inactive"} />
                       </div>

                        <div className="mx-3">
                            <TextField
                                label="First Name"
                                type="text"
                                name="firstName"
                                isRequired={true}
                            />
                            <TextField
                                label="Last Name"
                                type="text"
                                name="lastName"
                                isRequired={true}
                            />
                        </div>

                        <Label style={generalStyles.sectionHeader} className="border-bottom my-3 pb-2 w-100">System Information</Label>
                        <div className="mx-3">
                            <TextField
                                label="User Name"
                                type="text"
                                name="userName"
                                isRequired={true}
                            />
                            <Autocomplete
                                name="roleId"
                                label="Role"
                                isRequired={true}
                                disabled={!IsNew() }
                                options={lookupOptions ?? []}
                                defaultSelected={
                                    initialVal.roleId != null && initialVal.roleId.length > 0
                                        ? [initialVal.roleId[0]]
                                        : []
                                }
                            />
                        </div>

                        <Label style={generalStyles.sectionHeader} className="border-bottom my-3 pb-2 w-100">Contact Information</Label>
                        <div className="mx-3">
                            <TextField
                                label="Email Address"
                                type="text"
                                name="email"
                                isRequired={true}
                            />
                            <MaskedPhoneNumberField
                                name="phoneNumber"
                                label="Phone Number"
                                isRequired={true}
                            />
                        </div>
                        
                        <div className="mt-5">
                            <ActionBar
                                submitHandler={saveInput}
                                cancelHandler={onClose}
                                submitText="Submit"
                                cancelText="Cancel"
                                isLoading={status === "pending"}
                            ></ActionBar>
                        </div>
                    </Form>
                )}
            </Formik>
            <ConfirmationDialog
                open={isConfirmationDialogOpen}
                onClose={toggleConfirmationDialogHandler}
                onConfirm={handleConfirmationDialog}
                contentTitle={`Update User Name`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            ></ConfirmationDialog>
        </FormBackGround>
    );
};

export default UserForm;
